import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";

interface SaleSearchSale {
  OrderNumber: string;
  Status: string;
  Customer: string;
  InvoiceNumber: string | null;
  CustomerReference: string | null;
  InvoiceAmount: number;
  OrderDate: string;
}

interface SaleSearchResult {
  search: string;
  ok: boolean;
  total: number;
  sales?: SaleSearchSale[];
}

interface Row {
  id: string;
  search: string;
  orderNumber: string;
  status: string;
  customer: string;
  invoiceNumber: string | null;
  customerReference: string | null;
  invoiceAmount: number | null;
  orderDate: string;
}

const toRows = (results: SaleSearchResult[]): Row[] => {
  const rows: Row[] = [];
  results.forEach((result, i) => {
    if (!result.sales || result.sales.length === 0) {
      rows.push({
        id: `${i}-none`,
        search: result.search,
        orderNumber: "",
        status: "",
        customer: "",
        invoiceNumber: null,
        customerReference: null,
        invoiceAmount: null,
        orderDate: "",
      });
      return;
    }
    result.sales.forEach((sale, j) => {
      rows.push({
        id: `${i}-${j}`,
        search: result.search,
        orderNumber: sale.OrderNumber,
        status: sale.Status,
        customer: sale.Customer,
        invoiceNumber: sale.InvoiceNumber,
        customerReference: sale.CustomerReference,
        invoiceAmount: sale.InvoiceAmount,
        orderDate: sale.OrderDate,
      });
    });
  });
  return rows;
};

const COLUMNS: GridColDef<Row>[] = [
  { field: "search", headerName: "Search", width: 160 },
  { field: "orderNumber", headerName: "Order #", width: 120 },
  { field: "status", headerName: "Status", width: 120 },
  { field: "customer", headerName: "Customer", width: 260, flex: 1 },
  { field: "invoiceNumber", headerName: "Invoice #", width: 120 },
  { field: "customerReference", headerName: "Customer reference", width: 160 },
  { field: "invoiceAmount", headerName: "Invoice amount", width: 130, type: "number" },
  { field: "orderDate", headerName: "Order date", width: 180 },
];

/** Scratch grid of sale-search-results.json (getSaleBySearch over customer references, served from ui/public/): one row per matched sale. */
export const Sandbox = () => {
  const [rows, setRows] = useState<Row[] | null>(null);

  useEffect(() => {
    fetch("/sale-search-results.json")
      .then((res) => res.json())
      .then((data: SaleSearchResult[]) => setRows(toRows(data)));
  }, []);

  if (!rows) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 32px)",
        p: 2,
      }}
    >
      <DataGrid
        rows={rows}
        columns={COLUMNS}
        density="compact"
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
      />
    </Box>
  );
};
